import { Button } from "../ui/button";
import { X, Plane, Hotel, Ticket, ArrowRight, Loader2 } from "lucide-react";
import { format, isValid } from "date-fns";

/**
 * Trip Roadmap Overlay - Shows booking summary before checkout
 */
const TripRoadmapOverlay = ({ 
  isOpen, 
  onClose, 
  onProceed, 
  tier, 
  event, 
  origin, 
  guests, 
  flight, 
  hotel, 
  hotelClass, 
  totalPrice, 
  isLoading 
}) => {
  if (!isOpen) return null;

  const tierLabels = {
    ticketOnly: "Event Access",
    ticketFlight: "Event + Flight",
    completeTrip: "Complete Experience"
  };

  // Quirky hotel copy based on class
  const hotelCopy = {
    budget: "Simple, clean, and cheap. You're only here to shower and crash for 4 hours anyway. Save the cash for the bar.",
    mid: "The sweet spot. Comfy beds, decent shower, and soundproof enough that you won't hear your own ears ringing.",
    luxury: "Full-send mode. Plush robes, late checkout, and a spa to bring you back to life after the sun comes up."
  };

  // Safe date formatting
  const formatEventDate = () => {
    if (!event?.start_at) return "TBC";
    try {
      const date = new Date(event.start_at);
      return isValid(date) ? format(date, "EEE, MMM d") : "TBC";
    } catch {
      return "TBC";
    }
  };

  return (
    <div className="hype-overlay fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 5000 }} data-modal="true">
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl glass-strong rounded-2xl overflow-hidden animate-in fade-in zoom-in duration-300" style={{ zIndex: 5001 }}>
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full glass-solid flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          style={{ zIndex: 5002 }}
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="text-xs text-primary uppercase tracking-wider mb-1" style={{ fontFamily: "'Space Mono', monospace" }}>
            Your Trip Roadmap
          </div>
          <h2 className="text-2xl font-bold text-foreground">
            {tierLabels[tier] || "Experience"}
          </h2>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Event */}
          <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Ticket className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Event</div>
              <div className="font-bold text-foreground">{event?.name || "Event"}</div>
              <div className="text-sm text-muted-foreground">
                {event?.venue_name}, {event?.venue_city} • {formatEventDate()}
              </div>
            </div>
          </div>
          
          {/* Flight - Only show for ticketFlight and completeTrip */}
          {(tier === "ticketFlight" || tier === "completeTrip") && flight && (
            <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <Plane className="w-6 h-6 text-cyan-400" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Flight</div>
                <div className="font-bold text-foreground">
                  {origin?.city || "Your City"} → {event?.venue_city || "Destination"}
                </div>
                <div className="text-sm text-muted-foreground">
                  {flight.airline || "Carrier TBC"} • Round-trip for {guests} guest{guests > 1 ? 's' : ''}
                </div>
              </div>
            </div>
          )}
          
          {/* Hotel - Only show for completeTrip */}
          {tier === "completeTrip" && hotel && (
            <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Hotel className="w-6 h-6 text-purple-400" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Hotel</div>
                <div className="font-bold text-foreground">
                  {hotel.name || "Hotel"}
                </div>
                <div className="text-sm text-muted-foreground">
                  {hotelClass?.charAt(0).toUpperCase() + hotelClass?.slice(1) || "Standard"} • {guests} guest{guests > 1 ? 's' : ''}
                </div>
                <div className="text-xs text-muted-foreground italic mt-2">
                  {hotelCopy[hotelClass] || hotelCopy.mid}
                </div>
              </div>
            </div>
          )}
          
          {/* Total */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-primary/10 border border-primary/30">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
              <div className="text-2xl font-black text-primary" style={{ fontFamily: "'Space Mono', monospace" }}>
                £{Math.round(totalPrice || 0)}
              </div>
            </div>
            <Button
              onClick={onProceed}
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8"
              data-testid="proceed-to-checkout-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripRoadmapOverlay;
